import React, { ComponentRef, forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'antd-v5';
import { v4 as uuidv4 } from 'uuid';
import { Pagination } from 'components/Pagination';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import { useFetchMyClientsOrders } from 'domains/order/Order.requests';
import { fetchMyClientsOrdersRequestSaga, getMyClientsOrdersPagination } from 'domains/order/Order.store';
import { getMyClientsOrdersTableColumns } from 'pages/MyOrdersPage/ClientsOrders/ClientsOrders.utils';
import { ClientsOrdersFilter } from 'pages/MyOrdersPage/ClientsOrders/Filter/ClientsOrdersFilter';
import { Flex } from 'UI';
import { getData, isLoading } from 'utils';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';

const ClientsOrdersTable = forwardRef<ComponentRef<typeof Table>>((_props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const myClientsOrdersState = useFetchMyClientsOrders();
  const ordersDataStatus = myClientsOrdersState.searchStatus;
  const ordersData = getData(myClientsOrdersState?.data)?.orders ?? [];
  const ordersTotal = getData(myClientsOrdersState?.data)?.total ?? 0;
  const pagination = useSelector(getMyClientsOrdersPagination);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  const loading = isLoading(ordersDataStatus);

  const tableKey = useMemo(
    () => uuidv4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordersDataStatus],
  );
  const tableColumns = useMemo(() => getMyClientsOrdersTableColumns(t), [t]);

  const handleChangePage = useCallback(
    (page: number, pageSize: number) => {
      if (isDelegationLoading) {
        return;
      }
      scrollToTopSmooth();
      dispatch(fetchMyClientsOrdersRequestSaga({ page, pageSize }));
    },
    [dispatch, isDelegationLoading],
  );

  return (
    <Flex direction={'column'} padding={'0 0 50px 0'}>
      <ClientsOrdersFilter />
      <Table
        key={tableKey}
        bordered
        ref={ref}
        columns={tableColumns}
        dataSource={ordersData}
        rowKey={'uniqueId'}
        loading={loading}
        pagination={false}
        data-cy={'all-orders-table'}
        onRow={(record) => ({ 'data-cy': `order-row-${record.orderNumber}` } as React.HTMLAttributes<HTMLElement>)}
      />
      {!!ordersTotal && (
        <Pagination
          total={ordersTotal}
          pageSize={pagination.pageSize}
          currentPage={pagination.page}
          onPageChange={handleChangePage}
        />
      )}
    </Flex>
  );
});

ClientsOrdersTable.displayName = 'ClientsOrdersTable';

export default ClientsOrdersTable;
